import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  SkeletonText,
} from '@carbon/react'
import { Bullhorn, Notification, Settings } from '@carbon/icons-react'
import { useAccessStore } from '@sphere/auth/data-access'
import {
  useAnnouncements,
  useMarkAnnouncementAsRead,
  useUnreadAnnouncementCount,
} from '@sphere/communication/data-access'
import type { AnnouncementQueryDto } from '@sphere/communication/domain'
import { NotificationFeedItem } from './NotificationFeedItem'
import styles from './NotificationsPage.module.scss'

type FilterMode = 'all' | 'unread'

export default function NotificationsPage() {
  const navigate = useNavigate()
  const bootstrapLoaded = useAccessStore(state => state.bootstrapLoaded)
  const scope = useAccessStore(state => state.scope)
  const [filter, setFilter] = useState<FilterMode>('unread')

  const contextEstablishmentId = scope?.establishmentIds?.[0]
  const contextClassId = scope?.classIds?.[0]

  const listParams = useMemo((): AnnouncementQueryDto => {
    const params: AnnouncementQueryDto = {
      limit: 50,
      contextEstablishmentId,
      contextClassId,
    }
    if (filter === 'unread') {
      params.unreadOnly = true
    }
    return params
  }, [filter, contextEstablishmentId, contextClassId])

  const { data: announcementsData, isLoading } = useAnnouncements(listParams, {
    enabled: bootstrapLoaded,
  })
  const { data: unreadCount = 0 } = useUnreadAnnouncementCount({
    enabled: bootstrapLoaded,
  })
  const markAsRead = useMarkAnnouncementAsRead()

  const announcements = announcementsData?.data ?? []

  const handleOpen = (announcementId: string, isRead: boolean) => {
    if (!isRead) {
      markAsRead.mutate(announcementId)
    }
  }

  return (
    <div className={styles.pageContainer}>
      <header className={styles.heroHeader}>
        <div className={styles.heroInner}>
          <div className={styles.breadcrumbs}>
            <Breadcrumb noTrailingSlash>
              <BreadcrumbItem onClick={() => navigate('/')}>
                <span style={{ cursor: 'pointer' }}>Accueil</span>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <span>Notifications</span>
              </BreadcrumbItem>
            </Breadcrumb>
          </div>

          <div className={styles.titleRow}>
            <div className={styles.titleBlock}>
              <div className={styles.titleIcon} aria-hidden>
                <Notification size={24} />
              </div>
              <div className={styles.titleText}>
                <h1>Notifications</h1>
                <p>
                  Retrouvez ici les annonces de votre établissement et de vos
                  classes.
                </p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <Button
                kind="ghost"
                size="md"
                renderIcon={Settings}
                as={Link}
                to="/settings/notifications"
              >
                Préférences
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className={styles.content}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <div className={styles.filterGroup} role="tablist" aria-label="Filtrer les notifications">
              <button
                type="button"
                role="tab"
                aria-selected={filter === 'unread'}
                className={`${styles.filterBtn} ${filter === 'unread' ? styles.filterBtnActive : ''}`}
                onClick={() => setFilter('unread')}
              >
                Non lues
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={filter === 'all'}
                className={`${styles.filterBtn} ${filter === 'all' ? styles.filterBtnActive : ''}`}
                onClick={() => setFilter('all')}
              >
                Toutes
              </button>
            </div>

            {unreadCount > 0 && (
              <span className={styles.unreadBadge}>
                <span className={styles.unreadDot} aria-hidden />
                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {isLoading ? (
            <div className={styles.skeletonList}>
              {[1, 2, 3].map(i => (
                <div key={i} className={styles.skeletonRow}>
                  <SkeletonText width="40%" heading />
                  <SkeletonText width="100%" />
                </div>
              ))}
            </div>
          ) : announcements.length === 0 ? (
            <div className={styles.emptyState}>
              <Bullhorn size={48} className={styles.emptyIcon} />
              <h2 className={styles.emptyTitle}>
                {filter === 'unread'
                  ? 'Aucune notification non lue'
                  : 'Aucune notification'}
              </h2>
              <p className={styles.emptyDescription}>
                {filter === 'unread'
                  ? 'Vous êtes à jour — toutes vos annonces ont été lues.'
                  : 'Les annonces de vos professeurs et de votre établissement apparaîtront ici.'}
              </p>
            </div>
          ) : (
            <div className={styles.list}>
              {announcements.map(announcement => (
                <NotificationFeedItem
                  key={announcement.id}
                  announcement={announcement}
                  onOpen={() =>
                    handleOpen(announcement.id, announcement.isRead)
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
